import { useCallback, useMemo, useRef, useState } from 'react'
import type { MouseEvent } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import clsx from 'clsx'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import MessagingLabel from 'LuxKit/Label/MessagingLabel'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import CartReviewTile from 'components/Pages/CartReviewPage/CartReviewTile'
import CartReviewTileContent from 'components/Pages/CartReviewPage/CartReviewTileContent'
import CartReviewPriceStack from 'components/Pages/CartReviewPage/CartReviewPriceStack'
import LoadingCartReviewTileSkeleton from 'components/Pages/CartReviewPage/LoadingCartReviewTileSkeleton'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import ExpiryTimer from 'components/Pages/CartReviewPage/ExpiryTimer'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import { didClickInteractableElement } from 'components/Common/NestableClickable/NestableClickable'
import { isLeFlightSavedItemData, type LeFlightSavedItemData } from 'tripPlanner/api/types'
import { isFlightItem } from 'lib/checkout/checkoutUtils'
import noop from 'lib/function/noop'
import { FlightViewTypes } from 'constants/flight'
import { INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { formatMiddleDotSeparator } from 'LuxKit/intl/separators'
import { getFlightReviewDateSummary, getFlightReviewOccupancy, getFlightReviewTitle } from 'components/Pages/UserCartPage/SharedCarts/ReviewTiles/flightReviewTileUtils'
import type { CartPanelFlightItem } from '../hooks/useCartPanelItems'
import { getDateFromDateTime, isSavedFlightSearchExpired, parseFlightDurationParts } from '../utils/flightCartPanelUtils'
import { parseSearchParams } from './ShortlistedFlightsSection/flightShortlistUtils'
import { cartPanelMobileTileFullBleedStyles } from './cartPanelLayout.styles'
import CartPanelFlightLegExtensions from './CartPanelFlightLegExtensions'
import CartPanelFlightMobileTileLayout from './CartPanelFlightMobileTileLayout'
import useCartPanelDesktopTileLayout from './useCartPanelDesktopTileLayout'

const ResponsiveTileRoot = styled.div`
  width: 100%;
`

const TileWrapper = styled.div`
  position: relative;
  overflow: hidden;
  ${cartPanelMobileTileFullBleedStyles}
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  transition: box-shadow 0.15s ease, transform 0.15s ease;

  ${mediaQueryUp.tablet} {
    border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }

  &.clickable:hover {
    box-shadow: 0 ${rem(4)} ${rem(12)} rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }

  &.awaiting-fare {
    cursor: default;
  }

  &.deselected {
    opacity: 0.5;
  }

  &.clickable {
    cursor: pointer;
  }

  &.has-checkbox [data-testid="cart-review-tile"] > * > .without-image {
    padding-inline-start: ${rem(48)};
  }
`

const PositionedContent = styled.div`
  position: relative;
`

const TileInner = styled(CartReviewTile)`
  border-radius: 0;

  ${mediaQueryUp.tablet} {
    border: none;
  }
`

const CheckboxOverlay = styled.div`
  position: absolute;
  top: ${rem(22)};
  inset-inline-start: ${rem(16)};
  z-index: 1;
  line-height: 0;
`

const EMPTY_FLIGHT_EXTRAS: App.Checkout.FlightItemPaxBaggageMap = {
  baggage: {},
  carryOnBaggage: {},
}

interface Props {
  item: CartPanelFlightItem
  onToggleInclude: (item: CartPanelFlightItem) => void
  onChooseFlight: (item: CartPanelFlightItem) => void | Promise<void>
  onRefreshFlight: (item: CartPanelFlightItem) => void
  onChangeFlight: (item: CartPanelFlightItem) => void
  onViewFlightDetails: (item: CartPanelFlightItem) => void
  onDelete: (item: CartPanelFlightItem) => void
  isStreaming: boolean
  isRefreshing: boolean
}

function formatFlightDate(date: string | undefined, locale: string): string | undefined {
  if (!date) return undefined
  return intlDateFormatter(date, INTL_DMY_CASUAL_SHORT_FORMAT, locale)
}

function parseFlightDuration(duration: string | undefined): App.Checkout.FlightItemFlight['duration'] {
  const { hours, minutes } = parseFlightDurationParts(duration)
  if (hours === 0 && minutes === 0) return undefined
  return { hours, minutes }
}

function getFlightDateTime(date: string | undefined, time: string | undefined): string | undefined {
  if (!time) return undefined
  if (time.match(/^\d{4}-\d{2}-\d{2}/)) return time
  if (!date) return time
  return `${date}T${time}`
}

function getProjectedFlightLeg(
  savedItemData: LeFlightSavedItemData | undefined,
  fallbackLegOrder: number,
): App.Checkout.FlightItemFlight | undefined {
  if (!savedItemData?.departureAirport || !savedItemData.arrivalAirport) return undefined

  const legOrder = savedItemData.legOrder ?? fallbackLegOrder
  const routeKey = `${savedItemData.departureAirport}-${savedItemData.arrivalAirport}-${legOrder}`
  const journeyKey = savedItemData.journeyKey ?? routeKey
  const departureDateTime = getFlightDateTime(savedItemData.departureDate, savedItemData.departureTime)
  const arrivalDateTime = getFlightDateTime(savedItemData.arrivalDate, savedItemData.arrivalTime)

  return {
    journeyId: savedItemData.flightGroupId ?? savedItemData.searchId ?? journeyKey,
    journeyKey,
    extras: EMPTY_FLIGHT_EXTRAS,
    provider: savedItemData.source,
    providerSearchId: savedItemData.searchId ?? '',
    carrier: savedItemData.airline ?? '',
    carrierImage: savedItemData.carrierLogo ? {
      url: savedItemData.carrierLogo,
      title: savedItemData.airline,
    } : undefined,
    departingAirportCode: savedItemData.departureAirport,
    departingAirportName: savedItemData.departureAirport,
    arrivalAirportCode: savedItemData.arrivalAirport,
    arrivalAirportName: savedItemData.arrivalAirport,
    departingDate: savedItemData.departureDate ?? getDateFromDateTime(savedItemData.departureTime),
    arrivalDateTime,
    departureDateTime,
    duration: parseFlightDuration(savedItemData.duration),
    numberOfStops: savedItemData.layovers?.length ?? 0,
  }
}

const flightTileMessages = defineMessages({
  route: {
    id: 'D8mhQ7bT7yOmwj96xod1',
    defaultMessage: '{origin} to {destination}',
    description: 'Flight route on the trip planner cart panel flight tile, from the origin airport or city to the destination airport or city',
  },
  dateRoute: {
    id: 'i346sjvS0bfK1kOZ-lRn',
    defaultMessage: '{date} · {route}',
    description: 'Trip planner cart panel flight tile summary: the departure date followed by the flight route such as "SYD to MEL"',
  },
  dateRangeRoute: {
    id: 'PzNKYRWuEidX8GRFKtvv',
    defaultMessage: '{startDate} - {endDate} · {route}',
    description: 'Trip planner cart panel flight tile summary: the first and last departure dates followed by the flight route such as "SYD to MEL"',
  },
})

function CartPanelFlightTile({
  item,
  onToggleInclude,
  onChooseFlight,
  onRefreshFlight,
  onChangeFlight,
  onViewFlightDetails,
  onDelete,
  isStreaming,
  isRefreshing,
}: Props) {
  const intl = useIntl()
  const [isChooseFlightPending, setIsChooseFlightPending] = useState(false)

  const handleDelete = useCallback((e?: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    e?.preventDefault()
    e?.stopPropagation()
    onDelete(item)
  }, [onDelete, item])

  const handleChooseFlight = useCallback(() => {
    if (isChooseFlightPending) return
    const shouldShowPending = item.cartStatus === 'needs_flight_selection'
    if (shouldShowPending) {
      setIsChooseFlightPending(true)
    }

    try {
      const result = onChooseFlight(item)
      if (shouldShowPending) {
        Promise.resolve(result)
          .catch(noop)
          .finally(() => setIsChooseFlightPending(false))
      }
    } catch(error) {
      if (shouldShowPending) {
        setIsChooseFlightPending(false)
      }
      throw error
    }
  }, [isChooseFlightPending, item, onChooseFlight])

  const handleRefreshFlight = useCallback(() => {
    onRefreshFlight(item)
  }, [onRefreshFlight, item])

  const handleChangeFlight = useCallback(() => {
    onChangeFlight(item)
  }, [onChangeFlight, item])

  const handleToggleInclude = useCallback(() => {
    onToggleInclude(item)
  }, [item, onToggleInclude])

  const handleCheckboxClick = useCallback((e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
  }, [])

  const activeFlightItem = item.flightCartItem && isFlightItem(item.flightCartItem.item)
    ? item.flightCartItem.item
    : undefined
  const activeFlightExpiry = activeFlightItem?.expiry
  const flightLegs = activeFlightItem?.flights
  const isAwaitingFareSelection = item.cartStatus === 'needs_flight_selection'
  const savedFlightExpiry = isLeFlightSavedItemData(item.savedItemData) ? item.savedItemData.searchExpiry : undefined
  // Either leg lapsing invalidates the pending selection - one search prices a return
  // pair, and resolveSavedFlightSelection rejects the saved selection when either half
  // has lapsed. So both the countdown and the banner below read both legs: otherwise a
  // return trip whose return search died keeps ticking down against a live outbound
  // while offering "Select fares" against a search that can no longer produce one.
  const isSavedSearchExpired = isSavedFlightSearchExpired(item)
  const pendingFlightExpiry = isAwaitingFareSelection &&
    savedFlightExpiry !== undefined &&
    !isSavedSearchExpired ?
    savedFlightExpiry :
    undefined
  const loadingLabel = intl.formatMessage({
    id: 'GxqE7ekYQ1AdKaHwDzeR',
    defaultMessage: 'Loading...',
    description: 'Loading indicator text while trip data is being fetched',
  })

  const parsedSearchParams = useMemo(() => {
    if (!isLeFlightSavedItemData(item.savedItemData)) return undefined
    return parseSearchParams(item.savedItemData.searchParams)
  }, [item.savedItemData])

  const projectedFlightLegs = useMemo(() => {
    if (!isLeFlightSavedItemData(item.savedItemData)) return undefined

    return [
      getProjectedFlightLeg(item.savedItemData, 1),
      getProjectedFlightLeg(item.returnSavedItemData, 2),
    ].filter((leg): leg is App.Checkout.FlightItemFlight => !!leg)
  }, [item.returnSavedItemData, item.savedItemData])

  // For a grouped multi-city itinerary, build the timeline from every leg (each
  // leg uses its own active cart flights when converted, else a projected leg
  // from saved data) and use the soonest leg expiry for the countdown.
  const multiCityDisplay = useMemo(() => {
    if (!item.multiCityLegs) return undefined
    const legs: Array<App.Checkout.FlightItemFlight> = []
    const expiries: Array<number> = []
    item.multiCityLegs.forEach((leg, index) => {
      const legActive = leg.flightCartItem && isFlightItem(leg.flightCartItem.item)
        ? leg.flightCartItem.item
        : undefined
      if (legActive?.flights?.length) {
        legs.push(...legActive.flights)
        if (legActive.expiry !== undefined) expiries.push(legActive.expiry)
      } else if (isLeFlightSavedItemData(leg.savedItemData)) {
        const projected = getProjectedFlightLeg(leg.savedItemData, index + 1)
        if (projected) legs.push(projected)
        if (leg.savedItemData.searchExpiry !== undefined) expiries.push(leg.savedItemData.searchExpiry)
      }
    })
    return { legs, expiry: expiries.length > 0 ? Math.min(...expiries) : undefined }
  }, [item.multiCityLegs])

  const displayFlightLegs = multiCityDisplay?.legs ?? flightLegs ?? projectedFlightLegs
  const effectiveViewType = item.multiCityLegs
    ? FlightViewTypes.MULTI_CITY
    : (activeFlightItem?.viewType ?? item.flightCartItem?.viewType)

  const displayFlightExpiry = multiCityDisplay
    ? multiCityDisplay.expiry
    : (activeFlightExpiry ?? pendingFlightExpiry)
  const showCountdown = !item.isAvailabilityBlocked && displayFlightExpiry !== undefined

  const routeLabel = useMemo(() => {
    const firstLeg = parsedSearchParams?.itinerary[0]
    const origin = firstLeg?.departureAirport ?? item.savedItemData.departureAirport
    const destination = firstLeg?.arrivalAirport ?? item.savedItemData.arrivalAirport
    if (!origin || !destination) return undefined
    return intl.formatMessage(flightTileMessages.route, { origin, destination })
  }, [intl, item.savedItemData, parsedSearchParams])

  const returnRouteLabel = useMemo(() => {
    const returnLeg = parsedSearchParams?.itinerary[1]
    const returning = item.returnSavedItemData
    const origin = returnLeg?.departureAirport ?? returning?.departureAirport
    const destination = returnLeg?.arrivalAirport ?? returning?.arrivalAirport
    if (!origin || !destination) return undefined
    return intl.formatMessage(flightTileMessages.route, { origin, destination })
  }, [intl, item.returnSavedItemData, parsedSearchParams])

  const tileTitle = useMemo(() => {
    return getFlightReviewTitle(
      intl,
      effectiveViewType,
      !!item.returnOption || !!item.returnSavedItemData,
    )
  }, [effectiveViewType, intl, item.returnOption, item.returnSavedItemData])

  const tileDates = useMemo(() => {
    const firstFlight = displayFlightLegs?.[0]
    if (firstFlight) {
      const reviewDateSummary = getFlightReviewDateSummary(intl, displayFlightLegs, effectiveViewType)
      if (reviewDateSummary) return reviewDateSummary

      const startDate = formatFlightDate(firstFlight.departingDate, intl.locale)
      const origin = firstFlight.departingAirportName ?? firstFlight.departingAirportCode
      const destination = firstFlight.arrivalAirportName ?? firstFlight.arrivalAirportCode

      if (!startDate || !origin || !destination) return routeLabel

      if (
        displayFlightLegs.length > 1 ||
        effectiveViewType === FlightViewTypes.TWO_ONE_WAYS ||
        effectiveViewType === FlightViewTypes.TWO_ONE_WAYS_AND_RETURN ||
        effectiveViewType === FlightViewTypes.RETURN ||
        effectiveViewType === FlightViewTypes.MULTI_CITY
      ) {
        const lastFlight = displayFlightLegs[displayFlightLegs.length - 1]
        const endDate = formatFlightDate(lastFlight?.departingDate, intl.locale)
        const toCity = effectiveViewType === FlightViewTypes.MULTI_CITY
          ? (lastFlight?.arrivalAirportName ?? lastFlight?.arrivalAirportCode ?? destination)
          : destination

        return endDate
          ? intl.formatMessage(flightTileMessages.dateRangeRoute, {
              startDate,
              endDate,
              route: intl.formatMessage(flightTileMessages.route, { origin, destination: toCity }),
            })
          : intl.formatMessage(flightTileMessages.dateRoute, {
              date: startDate,
              route: intl.formatMessage(flightTileMessages.route, { origin, destination }),
            })
      }

      return intl.formatMessage(flightTileMessages.dateRoute, {
        date: startDate,
        route: intl.formatMessage(flightTileMessages.route, { origin, destination }),
      })
    }

    const firstLeg = parsedSearchParams?.itinerary[0]
    const returnLeg = parsedSearchParams?.itinerary[1]
    const startDate = formatFlightDate(firstLeg?.departureDate ?? item.savedItemData.departureDate ?? item.savedItemData.departureTime?.split('T')[0], intl.locale)
    const endDate = formatFlightDate(returnLeg?.departureDate ?? item.returnSavedItemData?.departureDate ?? item.returnSavedItemData?.departureTime?.split('T')[0], intl.locale)

    if (startDate && endDate && routeLabel) {
      return intl.formatMessage(flightTileMessages.dateRangeRoute, { startDate, endDate, route: routeLabel })
    }

    if (startDate && routeLabel) {
      return intl.formatMessage(flightTileMessages.dateRoute, { date: startDate, route: routeLabel })
    }

    return [routeLabel, returnRouteLabel].filter(Boolean).join(formatMiddleDotSeparator(intl))
  }, [
    effectiveViewType,
    displayFlightLegs,
    intl,
    item.returnSavedItemData,
    item.savedItemData,
    parsedSearchParams,
    returnRouteLabel,
    routeLabel,
  ])

  const statusContent = useMemo(() => {
    if (item.isAvailabilityBlocked) {
      const isPastDate = item.flightCartItem?.status === 'dates_in_past'
      const isUnavailable = item.flightCartItem?.status === 'unavailable'
      const shouldSearchForReplacement = isPastDate || isUnavailable || item.hasRefreshFailed
      let label: string
      let banner: string
      let action: string
      let onAction: () => void

      if (isPastDate) {
        label = intl.formatMessage({
          id: 'J8r6gZvNBK8Mq4Ny7oOu',
          defaultMessage: 'Flight dates in the past',
          description: 'Attention indicator shown when a TripChat cart flight is for past dates',
        })
        banner = intl.formatMessage({
          id: 'O4ACypR4Y-7wgqg49xMW',
          defaultMessage: 'Your selected dates are in the past.',
          description: 'Warning banner shown when a TripChat cart flight has past dates',
        })
      } else if (shouldSearchForReplacement) {
        label = intl.formatMessage({
          id: '4gHq7Z-820YmOiV_60Z_',
          defaultMessage: 'Flight unavailable',
          description: 'Attention indicator shown when a TripChat cart flight is unavailable',
        })
        banner = intl.formatMessage({
          id: '9jyP-CKrbp3Umb1Mbqta',
          defaultMessage: "We couldn't refresh your flight.",
          description: 'Warning banner shown when a TripChat cart flight cannot be refreshed',
        })
      } else {
        label = intl.formatMessage({
          id: 'OHKYOzwcRPzDdP1VZuy7',
          defaultMessage: 'Flight price expired',
          description: 'Attention indicator shown when a TripChat cart flight is expired or unavailable',
        })
        banner = intl.formatMessage({
          id: 'PWk-7aoXLS6AEnorUvDp',
          defaultMessage: 'This flight price has expired. Refresh the search to continue.',
          description: 'Warning banner shown when a TripChat cart flight is expired',
        })
      }

      if (shouldSearchForReplacement) {
        action = intl.formatMessage({
          id: '_UqqvrffchEPRogC5KWI',
          defaultMessage: 'Search for flights',
          description: 'Button label to search for a replacement TripChat cart flight',
        })
        onAction = handleChangeFlight
      } else {
        action = intl.formatMessage({
          id: 'Jh-CAGCQUEuBbstfiO9X',
          defaultMessage: 'Refresh search',
          description: 'Button label to refresh an expired TripChat cart flight',
        })
        onAction = handleRefreshFlight
      }

      return {
        label,
        banner,
        action,
        onAction,
        isRefreshAction: !shouldSearchForReplacement,
      }
    }

    if (item.cartStatus === 'needs_flight_offer') {
      return {
        label: intl.formatMessage({
          id: 'S_d6JoNXtKoCnyK9qaXA',
          defaultMessage: 'Flight offer needed',
          description: 'Attention indicator prompting the user to choose an exact flight offer',
        }),
        action: intl.formatMessage({
          id: 'Pa0mncAzwBO5UPqryVCl',
          defaultMessage: 'Choose flight',
          description: 'Button label to choose an exact flight from a TripChat cart prompt',
        }),
        onAction: handleChooseFlight,
      }
    }

    if (item.cartStatus === 'needs_flight_selection') {
      const savedData = isLeFlightSavedItemData(item.savedItemData) ? item.savedItemData : undefined
      const isExpired = isSavedSearchExpired
      const isMultiCity = savedData?.flightGroupType === 'multi_city'

      // Multi-city legs have no fare to choose — they auto-convert straight from
      // saved data (see the auto-convert effect in useCartPanel). So they must
      // never show the "Select fares" banner; the tile stays clean (price +
      // countdown) while it converts, matching the offer page where multi-city
      // flights are added as complete items. We still expose a quiet "Refresh
      // search" button (rendered as a secondary action, not a warning banner) so
      // there's always a manual recourse if the auto-convert can't complete.
      // Expired saved searches fall through to the standard refresh path below.
      if (isMultiCity && !isExpired) {
        return {
          action: intl.formatMessage({
            id: 'Jh-CAGCQUEuBbstfiO9X',
            defaultMessage: 'Refresh search',
            description: 'Button label to refresh an expired TripChat cart flight',
          }),
          onAction: handleRefreshFlight,
        }
      }

      let label: string
      let action: string
      let onAction: () => void

      if (isExpired) {
        label = intl.formatMessage({
          id: 'l_qj-dmqmC1QuZQrp6zn',
          defaultMessage: 'Flight price expired',
          description: 'Attention indicator shown when a saved TripChat flight estimate has expired',
        })
        action = intl.formatMessage({
          id: 'Jh-CAGCQUEuBbstfiO9X',
          defaultMessage: 'Refresh search',
          description: 'Button label to refresh an expired TripChat cart flight',
        })
        onAction = handleRefreshFlight
        return {
          label,
          banner: intl.formatMessage({
            id: '4N_-Lap2Ky3rkWomu9Ad',
            defaultMessage: 'This flight price has expired. Refresh the search to continue.',
            description: 'Warning banner shown when a saved TripChat flight search has expired during fare selection',
          }),
          action,
          onAction,
          isRefreshAction: true,
        }
      } else {
        label = intl.formatMessage({
          id: 'o8BHvuttOPSingB8tuwI',
          defaultMessage: 'Fare selection needed',
          description: 'Attention indicator prompting the user to select an exact flight fare',
        })
        action = intl.formatMessage({
          id: '70zTxFFR9-x4jshP4iUZ',
          defaultMessage: 'Select fares',
          description: 'Button label to select an exact fare for a TripChat cart flight',
        })
        onAction = handleChooseFlight
      }

      if (isChooseFlightPending) {
        action = loadingLabel
      }

      return {
        label,
        banner: intl.formatMessage({
          id: 'W3atqQTjHixcjahmUK7n',
          defaultMessage: 'Select your fares to continue booking this flight.',
          description: 'Warning banner shown when a TripChat cart flight requires fare selection',
        }),
        action,
        onAction,
        isRefreshAction: true,
      }
    }

    if (item.cartStatus === 'active') {
      return {
        action: intl.formatMessage({
          id: 'zkkrjGNXRdPMP1Ej6gY4',
          defaultMessage: 'Change flight',
          description: 'Button label to change an active TripChat cart flight',
        }),
        onAction: handleChangeFlight,
      }
    }

    return undefined
  }, [
    handleChangeFlight,
    handleChooseFlight,
    handleRefreshFlight,
    intl,
    isChooseFlightPending,
    item.cartStatus,
    item.flightCartItem?.status,
    item.hasRefreshFailed,
    item.isAvailabilityBlocked,
    item.savedItemData,
    isSavedSearchExpired,
    loadingLabel,
  ])

  const changeAction = useMemo(() => statusContent?.action && !statusContent.isRefreshAction ? (
    <TextButton
      kind="secondary"
      size="small"
      shape="round"
      onClick={statusContent.onAction}
      disabled={isStreaming || isChooseFlightPending}
    >
      {statusContent.action}
    </TextButton>
  ) : null, [isChooseFlightPending, isStreaming, statusContent])

  const hasFlightLegs = displayFlightLegs && displayFlightLegs.length > 0

  const extension = useMemo(() => hasFlightLegs ? (
    <CartPanelFlightLegExtensions
      flights={displayFlightLegs}
      isClassicReward={activeFlightItem?.isClassicReward ?? false}
      hideFareDetails={isAwaitingFareSelection}
    />
  ) : undefined, [activeFlightItem?.isClassicReward, displayFlightLegs, hasFlightLegs, isAwaitingFareSelection])

  const mobileExtension = useMemo(() => hasFlightLegs ? (
    <CartPanelFlightLegExtensions
      flights={displayFlightLegs}
      isClassicReward={activeFlightItem?.isClassicReward ?? false}
      hideFareDetails={isAwaitingFareSelection}
      useTripCartPadding
    />
  ) : undefined, [activeFlightItem?.isClassicReward, displayFlightLegs, hasFlightLegs, isAwaitingFareSelection])

  const availabilityBannerActionProps = useMemo(() => statusContent?.isRefreshAction && statusContent.action ? {
    children: statusContent.action,
    onClick: statusContent.onAction,
    disabled: isStreaming || isChooseFlightPending,
    size: 'small' as const,
  } : undefined, [isChooseFlightPending, isStreaming, statusContent])

  const availabilityBanner = useMemo(() => statusContent?.banner ? (
    <MessageBanner
      kind="warning"
      density="compact"
      primaryActionButtonProps={availabilityBannerActionProps}
    >
      {statusContent.banner}
    </MessageBanner>
  ) : undefined, [availabilityBannerActionProps, statusContent])

  const attentionLabel = useMemo(() => statusContent?.label && !statusContent.banner ? (
    <MessagingLabel variant="warning">
      {statusContent.label}
    </MessagingLabel>
  ) : undefined, [statusContent])

  const tileLabel = useMemo(() => showCountdown
    ? <ExpiryTimer expiry={displayFlightExpiry} canShowExtension />
    : attentionLabel, [attentionLabel, displayFlightExpiry, showCountdown])

  const formattedOccupancy = useMemo(() => {
    const occupants = item.flightCartItem?.occupants ?? parsedSearchParams?.occupancies[0]
    return getFlightReviewOccupancy(intl, occupants) || undefined
  }, [item.flightCartItem, intl, parsedSearchParams])

  const priceContent = useMemo(() => {
    if (item.pricing.price === undefined) return undefined

    if (item.pricing.memberPrice !== undefined && item.cartStatus !== 'active') {
      return (
        <PriceRowLuxPlusPrice
          size="S"
          memberPrice={item.pricing.memberPrice}
          nonMemberPrice={item.pricing.price}
        />
      )
    }

    if (item.cartStatus === 'active') {
      return (
        <CartReviewPriceStack
          price={item.pricing.price}
          memberPrice={item.pricing.memberPrice}
          value={item.pricing.value ?? 0}
        />
      )
    }

    return (
      <PriceRowPrice
        price={item.pricing.price}
        priceFormat="preciseDollar"
        saleUnit=""
        size="S"
      />
    )
  }, [item.cartStatus, item.pricing.memberPrice, item.pricing.price, item.pricing.value])
  const price = priceContent && <Group direction="horizontal" gap={4} verticalAlign="end">
    {priceContent}
    <Caption variant="large">{intl.formatMessage({
      id: '6nJFt2Nnp0LMZrPK4Xuu',
      defaultMessage: 'total',
      description: 'Label for the whole-party flight price in the trip planner cart',
    })}
    </Caption>
  </Group>

  const deleteLabel = useMemo(() => intl.formatMessage({
    id: 'jDEZjhV3eo-cnxIBHOtR',
    defaultMessage: 'Delete',
    description: 'Button to permanently delete an item from the trip planner cart panel',
  }), [intl])

  const deleteAction = useMemo(() => (
    <TextButton
      kind="secondary"
      size="small"
      shape="round"
      startIcon={<LineTrashAltIcon />}
      onClick={handleDelete}
      disabled={isStreaming}
    >
      {deleteLabel}
    </TextButton>
  ), [deleteLabel, handleDelete, isStreaming])

  const includeCheckbox = useMemo(() => !item.isBooked ? (
    <CheckboxInput
      checked={!item.cartExcluded}
      onChange={handleToggleInclude}
      disabled={isStreaming}
      size="medium"
    />
  ) : null, [handleToggleInclude, isStreaming, item.cartExcluded, item.isBooked])

  const fallbackActions = useMemo(() => (
    <Group direction="horizontal" gap={8}>
      {changeAction}
      {deleteAction}
    </Group>
  ), [changeAction, deleteAction])

  const actions = useMemo(() => item.cartStatus === 'active' ? (
    <Group direction="horizontal" gap={8}>
      {item.isAvailabilityBlocked && changeAction}
      {deleteAction}
    </Group>
  ) : fallbackActions, [changeAction, deleteAction, fallbackActions, item.cartStatus, item.isAvailabilityBlocked])

  const handleTileClickCapture = useCallback((e: MouseEvent<HTMLDivElement>) => {
    // A grouped multi-city itinerary has no single backing cart item to open in
    // the details modal, so it isn't click-to-view.
    if (isStreaming || item.isBooked || item.cartStatus !== 'active' || item.multiCityLegs) return
    if (!(e.target instanceof HTMLElement)) return
    if (didClickInteractableElement(e.target, e.currentTarget)) return
    onViewFlightDetails(item)
  }, [onViewFlightDetails, isStreaming, item])

  const isClickable = item.cartStatus === 'active' && !item.isBooked && !isStreaming && !item.multiCityLegs

  const metaData = useMemo(() => !hasFlightLegs && item.savedItemData.airline
    ? <BodyText variant="medium" colour="neutral-three">{item.savedItemData.airline}</BodyText>
    : undefined, [hasFlightLegs, item.savedItemData.airline])

  const mobileMetaData = useMemo(() => {
    if (formattedOccupancy) {
      return <BodyText variant="medium" colour="light-foreground-alt">{formattedOccupancy}</BodyText>
    }
    return metaData
  }, [formattedOccupancy, metaData])

  const tileContent = useMemo(() => (
    <CartReviewTileContent
      title={tileTitle}
      dates={tileDates || false}
      metaData={metaData}
      occupancy={formattedOccupancy ?? false}
      label={tileLabel}
    />
  ), [formattedOccupancy, metaData, tileDates, tileLabel, tileTitle])
  const content = tileContent

  const rootRef = useRef<HTMLDivElement>(null)
  const isDesktopLayout = useCartPanelDesktopTileLayout(rootRef)

  const handleMobileCardClick = useCallback(() => {
    if (isStreaming || item.isBooked || item.cartStatus !== 'active' || item.multiCityLegs) return
    onViewFlightDetails(item)
  }, [isStreaming, item, onViewFlightDetails])

  if (isRefreshing) {
    return <LoadingCartReviewTileSkeleton />
  }

  return (
    <ResponsiveTileRoot ref={rootRef}>
      {isDesktopLayout ? (
        <div data-testid="cart-panel-flight-desktop-tile">
          <TileWrapper
            className={clsx({
              deselected: item.cartExcluded,
              clickable: isClickable,
              'awaiting-fare': isAwaitingFareSelection,
              'has-checkbox': !!includeCheckbox,
            })}
            onClickCapture={handleTileClickCapture}
          >
            {availabilityBanner}
            <PositionedContent>
              {includeCheckbox && (
                <CheckboxOverlay onClick={handleCheckboxClick}>
                  {includeCheckbox}
                </CheckboxOverlay>
              )}
              <TileInner
                banner={undefined}
                content={content}
                extension={extension}
                price={price}
                isBookable
                actions={actions}
              />
            </PositionedContent>
          </TileWrapper>
        </div>
      ) : (
        <CartPanelFlightMobileTileLayout
          item={item}
          onToggleInclude={onToggleInclude}
          onCardClick={isClickable ? handleMobileCardClick : undefined}
          layoutTitle={tileTitle}
          layoutSubtitle={tileDates}
          headerPrefix={tileLabel}
          mobileMetaData={mobileMetaData}
          topBanner={availabilityBanner}
          extension={mobileExtension}
          attention={undefined}
          footerActions={actions}
          price={price}
          isToggleDisabled={isStreaming}
          isAwaitingFareSelection={isAwaitingFareSelection}
        />
      )}
    </ResponsiveTileRoot>
  )
}

export default CartPanelFlightTile
